import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip } from "recharts";
import { TrendUp } from "@phosphor-icons/react";
import { useApi, money } from "@/lib/api";

const label = (ym) => new Date(`${ym}-15`).toLocaleString("en-US", { month: "short" });

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-md border border-[#2A2A2A] bg-[#050505] px-3 py-2 text-xs font-mono">
      <p className="text-[#A3A3A3]">{new Date(`${r.month}-15`).toLocaleString("en-US", { month: "long", year: "numeric" })}</p>
      <p className="text-[#635BFF]">MRR {money(r.mrr)}</p>
      <p className="text-[#00FF9D]">Collected {money(r.collected)}</p>
    </div>
  );
};

const Change = ({ months }) => {
  const first = months[0]?.mrr, last = months[months.length - 1]?.mrr;
  if (!first) return null;
  const pct = ((last - first) / first) * 100;
  return <span data-testid="revenue-trend-change" className={`font-mono text-[11px] ${pct >= 0 ? "text-[#00FF9D]" : "text-[#FF5C7A]"}`}>{pct >= 0 ? "+" : ""}{pct.toFixed(0)}% in 6 mo</span>;
};

export const RevenueTrend = ({ live }) => {
  const api = useApi();
  const { data, isError } = useQuery({ queryKey: ["revenue-trend"], queryFn: () => api.get("/stripe-connect/revenue-trend"), staleTime: 300000, enabled: !!live });
  if (!live || (data && !data.connected)) return null;
  const months = (data?.months || []).map((m) => ({ ...m, short: label(m.month) }));
  const now = months[months.length - 1];
  return (
    <div data-testid="revenue-trend-card" className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono text-[11px] text-[#A3A3A3] flex items-center gap-1"><TrendUp className="text-[#635BFF]" /> REVENUE · LAST 6 MONTHS</p>
          <p data-testid="revenue-trend-mrr" className="font-display text-2xl mt-1">{now ? money(now.mrr) : "—"} <span className="text-xs text-[#A3A3A3]">MRR</span></p>
        </div>
        {months.length > 0 && <Change months={months} />}
      </div>
      {isError ? <p data-testid="revenue-trend-error" className="mt-3 text-xs text-[#FF8FA3]">Couldn't load revenue history from Stripe right now.</p>
        : data && months.every((m) => !m.mrr && !m.collected) ? <p data-testid="revenue-trend-empty" className="mt-3 text-xs text-[#A3A3A3]">No subscription revenue or payments in the last 6 months yet.</p> : (
        <div className="h-40 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={months} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <XAxis dataKey="short" tick={{ fill: "#6B6B6B", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B6B6B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => money(v, true)} />
              <Tooltip content={<Tip />} cursor={{ fill: "#1A1A1A" }} />
              <Bar dataKey="collected" fill="#00FF9D" fillOpacity={0.35} radius={[3, 3, 0, 0]} />
              <Line dataKey="mrr" stroke="#635BFF" strokeWidth={2} dot={{ r: 2.5, fill: "#635BFF" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {!isError && !months.every((m) => !m.mrr && !m.collected) && <div className="mt-2 flex gap-4 text-[10px] text-[#A3A3A3]">
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 bg-[#635BFF]" /> Subscription MRR (month end)</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#00FF9D]/40" /> All money collected</span>
      </div>}
    </div>
  );
};
